import React, { useState } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, Input, Select, Typography, Button, Checkbox } from '@crisellevaldez/speculo-ui';
import {
  crmSetupSchema,
  CRMSetupData,
  CRMEntryData,
  CRM_OPTIONS,
  HIDDEN_CRM_OPTIONS,
  OFFERED_CRM_OPTIONS,
} from '../schemas/onboarding.schema';
import { HttpsUrlInput } from '../../../common/HttpsUrlInput';
import { FormSection } from '../components/FormSection';
import { TipBox } from '../components/TipBox';
import { QuestionLabel } from '../components/QuestionLabel';
import { NextButton } from '../components/NextButton';
import { StageContainer } from '../components/StageContainer';
import { InfoNotice } from '../components/InfoNotice';
import { useOnboardingWebhook } from '../hooks/useOnboardingWebhook';
import { TwoFaContactFields } from '../components/TwoFaContactFields';
import { useUser } from '../../../../hooks/useUser';
import { Organization } from '../../../../types/organization.types';
import { IntegrationsModal } from '../../client/IntegrationsModal';
import { isCrmConnectedViaRollout } from '../../../../utils/rollout';
import {
  connectsThroughRollout,
  CRM_TRADEMARK_NOTICE,
  isManualConnectCrm,
  MANUAL_CONNECT_CRMS,
  ManualConnectCrmId,
  ROLLOUT_CRMS,
  RolloutCrmId,
} from '../../../../constants/rolloutCrms';
import { getCrmConnectRequest } from '../../../../utils/manualCrmConnect';
import { ManualCrmConnectModal } from '../../client/ManualCrmConnectModal';
import { ExternalLink, Plus, X } from 'lucide-react';

interface Stage5CRMSetupProps {
  onNext: (data: CRMSetupData) => void;
  initialData?: Partial<CRMSetupData>;
  isLoading?: boolean;
  organization?: Organization;
  onFubSuccess?: () => void;
}

const CRM_AI_DISCLAIMER_COPY =
  'By checking the box and connecting your CRM and/or submitting data, you acknowledge and agree that you are solely responsible for ensuring that any contacts made available for outreach have provided appropriate consent to be contacted via automated systems, including AI-powered voice calls, dialing technology, text messaging, and email communications.\n\nYou further confirm that you have implemented appropriate tagging, filtering, and/or segmentation within your CRM to identify such contacts, and that only eligible contacts will be engaged by the system in compliance with all applicable laws, regulations, and communication consent requirements.';

const DEFAULT_CRM_ENTRY: CRMEntryData = {
  crm_type: '',
  crm_other_name: '',
  ai_disclaimer_acknowledged: false,
  super_admin_api_key: '',
  login_url: '',
  super_admin_username: '',
  super_admin_password: '',
  two_fa_contact_type: undefined,
  two_fa_contact_name: '',
  two_fa_contact_email: '',
  two_fa_contact_phone: '',
  brivity_website_login_url: '',
  brivity_username: '',
  brivity_password: '',
};

export const Stage5CRMSetup: React.FC<Stage5CRMSetupProps> = ({
  onNext,
  initialData,
  isLoading = false,
  organization,
  onFubSuccess,
}) => {
  // Rollout CRM whose Connect popup is open, if any.
  const [connectingCrm, setConnectingCrm] = useState<RolloutCrmId | null>(null);
  const [connectingCrmConsentAcknowledged, setConnectingCrmConsentAcknowledged] = useState(false);
  const { sendWebhook } = useOnboardingWebhook();
  const { user } = useUser();

  const form = useForm<CRMSetupData>({
    resolver: zodResolver(crmSetupSchema),
    defaultValues: initialData?.crm_entries?.length
      ? initialData
      : {
          crm_entries: [{ ...DEFAULT_CRM_ENTRY }],
        },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'crm_entries',
  });

  // Whether a Rollout CRM is connected — handles both the new `credentials[]`
  // shape and the legacy `<crm>.connected` shape.
  const crmSources = {
    crmConnections: organization?.crmConnections,
    rollout: organization?.rollout,
  };
  const isRolloutCrmConnected = (crm: RolloutCrmId) =>
    ROLLOUT_CRMS[crm].appKeys.some((appKey) => isCrmConnectedViaRollout(crmSources, appKey));
  // CRMs the team connects by hand count as done once the client submits them.
  const isCrmSubmitted = (crm: RolloutCrmId) =>
    isManualConnectCrm(crm) && Boolean(getCrmConnectRequest(organization?.onboardingData, crm));
  const isCrmConnected = (crm: RolloutCrmId) => isRolloutCrmConnected(crm) || isCrmSubmitted(crm);
  // CRM whose Speculo Connect form is open, if any.
  const [manualConnectCrm, setManualConnectCrm] = useState<ManualConnectCrmId | null>(null);

  const handleSubmit = async (data: CRMSetupData) => {
    sendWebhook('Stage 5 - Integrations (CRM Setup)', data);
    onNext(data);
  };

  const handleAddCRM = () => {
    append({ ...DEFAULT_CRM_ENTRY });
  };

  const handleRemoveCRM = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  // A Rollout CRM is the client's only CRM, and it must be connected before they continue.
  const selectedRolloutCrms = fields
    .map((_, index) => form.watch(`crm_entries.${index}.crm_type`))
    .filter(connectsThroughRollout);
  const hasRolloutCrmSelected = selectedRolloutCrms.length > 0;
  const pendingRolloutCrm = selectedRolloutCrms.find((crm) => !isCrmConnected(crm));

  return (
    <StageContainer
      title="CRM Integration"
      description="Connect your CRMs to sync contacts and activities with Speculo AI. You can add multiple CRMs if needed."
    >
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8" noValidate>
        {/* Info Notice */}
        <InfoNotice>
          Follow Up Boss, CINC, Lofty, BoldTrail and Sierra Interactive connect directly here. For
          all other CRMs, please provide the required information and our team will complete the
          setup.
        </InfoNotice>

        {/* CRM Entries */}
        {fields.map((field, index) => {
          const selectedCrm = form.watch(`crm_entries.${index}.crm_type`);
          const rolloutCrm = connectsThroughRollout(selectedCrm) ? selectedCrm : null;
          const isOther = selectedCrm === 'other';
          const isOtherCrm = selectedCrm && !rolloutCrm;
          const isAIDisclaimerAcknowledged = Boolean(
            form.watch(`crm_entries.${index}.ai_disclaimer_acknowledged`)
          );

          return (
            <div key={field.id} className="space-y-6">
              {/* Header */}
              <div className="flex items-center justify-between border-b pb-2">
                <Typography variant="h2" className="text-xl font-bold text-gray-900">
                  CRM {index + 1}
                </Typography>
                {fields.length > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleRemoveCRM(index)}
                    className="inline-flex items-center gap-1 text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <X className="w-3 h-3" />
                    Remove
                  </Button>
                )}
              </div>

              {/* CRM Selection */}
              <FormSection>
                <Form.Control required>
                  <QuestionLabel>What is the name of your primary CRM?</QuestionLabel>
                  <Controller
                    control={form.control}
                    name={`crm_entries.${index}.crm_type`}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={(HIDDEN_CRM_OPTIONS.includes(selectedCrm)
                          ? CRM_OPTIONS
                          : OFFERED_CRM_OPTIONS
                        ).map((opt) => ({ value: opt.value, label: opt.label }))}
                        placeholder="Select your CRM"
                        error={form.formState.errors.crm_entries?.[index]?.crm_type?.message}
                        searchable
                      />
                    )}
                  />
                </Form.Control>
              </FormSection>

              {/* Other CRM Name */}
              {isOther && (
                <FormSection>
                  <Form.Control required>
                    <QuestionLabel>
                      If "Other" was selected above, enter the name of your CRM:
                    </QuestionLabel>
                    <Input
                      {...form.register(`crm_entries.${index}.crm_other_name`)}
                      placeholder="e.g., Salesforce, HubSpot, etc."
                      error={form.formState.errors.crm_entries?.[index]?.crm_other_name?.message}
                    />
                  </Form.Control>
                </FormSection>
              )}

              {selectedCrm && (
                <FormSection>
                  <Controller
                    control={form.control}
                    name={`crm_entries.${index}.ai_disclaimer_acknowledged`}
                    render={({ field: { value, onChange } }) => (
                      <div className="space-y-2">
                        <div className="p-4 border border-amber-200 dark:border-amber-500/30 rounded-lg bg-amber-50 dark:bg-amber-900/10">
                          <div className="flex items-start gap-3">
                            <Checkbox
                              id={`crm-ai-disclaimer-${index}`}
                              checked={Boolean(value)}
                              onChange={(e) => onChange(e.target.checked)}
                            />
                            <label
                              htmlFor={`crm-ai-disclaimer-${index}`}
                              className="text-xs text-foreground/80 leading-relaxed cursor-pointer whitespace-pre-line"
                            >
                              {CRM_AI_DISCLAIMER_COPY}
                            </label>
                            <span className="text-red-600 dark:text-red-400 text-sm shrink-0">*</span>
                          </div>
                        </div>
                        {form.formState.errors.crm_entries?.[index]?.ai_disclaimer_acknowledged && (
                          <p className="text-sm text-red-600">
                            {
                              form.formState.errors.crm_entries?.[index]?.ai_disclaimer_acknowledged
                                ?.message
                            }
                          </p>
                        )}
                      </div>
                    )}
                  />
                </FormSection>
              )}

              {/* Rollout CRMs: connect through the Rollout Connect popup */}
              {rolloutCrm && isAIDisclaimerAcknowledged && (
                <>
                  <FormSection>
                    <div className="flex items-start gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      {rolloutCrm === 'fub' && (
                        <img src="/fub-icon.png" alt="Follow Up Boss" className="w-10 h-10" />
                      )}
                      {isManualConnectCrm(rolloutCrm) && (
                        <img
                          src={MANUAL_CONNECT_CRMS[rolloutCrm].icon}
                          alt={ROLLOUT_CRMS[rolloutCrm].label}
                          className="w-10 h-10"
                        />
                      )}
                      <div className="flex-1">
                        <Typography variant="h3" className="text-blue-900 mb-2">
                          {ROLLOUT_CRMS[rolloutCrm].label} Integration
                        </Typography>

                        {isCrmConnected(rolloutCrm) && (
                          <div className="flex items-center gap-2 mb-3">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-green-700 text-sm font-medium">
                              {isRolloutCrmConnected(rolloutCrm)
                                ? `${ROLLOUT_CRMS[rolloutCrm].label} is connected`
                                : `${ROLLOUT_CRMS[rolloutCrm].label} is submitted. Our team is finishing the connection.`}
                            </span>
                          </div>
                        )}

                        <p className="text-sm text-blue-900/80 mb-3">
                          {isManualConnectCrm(rolloutCrm)
                            ? MANUAL_CONNECT_CRMS[rolloutCrm].helperText
                            : ROLLOUT_CRMS[rolloutCrm].helperText}
                        </p>

                        <Button
                          type="button"
                          variant={isCrmConnected(rolloutCrm) ? 'outline' : 'primary'}
                          size="sm"
                          onClick={() => {
                            if (isManualConnectCrm(rolloutCrm)) setManualConnectCrm(rolloutCrm);
                            else {
                              setConnectingCrmConsentAcknowledged(isAIDisclaimerAcknowledged);
                              setConnectingCrm(rolloutCrm);
                            }
                          }}
                          disabled={!isAIDisclaimerAcknowledged}
                          className="inline-flex items-center gap-2 py-2.5"
                        >
                          <ExternalLink className="w-4 h-4" />
                          {isCrmConnected(rolloutCrm)
                            ? 'Update Configuration'
                            : ROLLOUT_CRMS[rolloutCrm].connectLabel}
                        </Button>
                      </div>
                    </div>
                    {isManualConnectCrm(rolloutCrm) && (
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {CRM_TRADEMARK_NOTICE}
                      </p>
                    )}
                    {rolloutCrm === 'fub' && (
                      <TipBox>
                        <strong>Pro tip:</strong> Before connecting your API, we highly recommend
                        applying the{' '}
                        <code className="bg-yellow-200 px-1 rounded">speculo_sync</code> tag to the
                        contacts you want Remi (AI) to engage with to help speed up the initial sync
                        process especially for large databases. This tag does not trigger any
                        communication. You can still move forward without applying the tag.
                        Additional contacts can be synced over at any time after the integration is
                        established.
                      </TipBox>
                    )}
                  </FormSection>

                  {/* Connecting needs no credentials, but we still need who handles 2FA codes. */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TwoFaContactFields
                      form={form}
                      fieldPrefix={`crm_entries.${index}.`}
                      user={user}
                      purpose="this CRM"
                    />
                  </div>
                </>
              )}

              {/* Non-FUB CRM Configuration */}
              {isOtherCrm && isAIDisclaimerAcknowledged && (
                <>
                  {/* API Key */}
                  <FormSection>
                    <Form.Control>
                      <QuestionLabel required={false}>
                        Account Owner (Owner/Super Admin) API Key
                      </QuestionLabel>
                      <Input
                        {...form.register(`crm_entries.${index}.super_admin_api_key`)}
                        placeholder="Enter your API key from the account owner"
                        error={
                          form.formState.errors.crm_entries?.[index]?.super_admin_api_key?.message
                        }
                      />
                      <TipBox>
                        <strong>Note:</strong> You must use the account owner (main admin)
                        credentials. This is the primary admin account with full CRM access. If your
                        CRM provides an API key, enter it here.
                      </TipBox>
                    </Form.Control>
                  </FormSection>

                  {/* Account access (required even with an API key) */}
                  <div className="space-y-4">
                    <Typography variant="h3" className="text-lg font-semibold text-gray-700">
                      Please provide the account owner login details:
                    </Typography>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormSection className="md:col-span-2">
                        <Form.Control required>
                          <Form.Label>Login URL</Form.Label>
                          <HttpsUrlInput
                            {...form.register(`crm_entries.${index}.login_url`)}
                            placeholder="yourcrm.com/login"
                            error={form.formState.errors.crm_entries?.[index]?.login_url?.message}
                          />
                          <Form.HelperText>Direct link to the CRM login page</Form.HelperText>
                        </Form.Control>
                      </FormSection>

                      <FormSection>
                        <Form.Control required>
                          <Form.Label>Account Owner Username</Form.Label>
                          <Input
                            {...form.register(`crm_entries.${index}.super_admin_username`)}
                            placeholder="Main admin account username"
                            error={
                              form.formState.errors.crm_entries?.[index]?.super_admin_username
                                ?.message
                            }
                          />
                          <Form.HelperText>
                            Username for the main admin account (account owner)
                          </Form.HelperText>
                        </Form.Control>
                      </FormSection>

                      <FormSection>
                        <Form.Control required>
                          <Form.Label>Account Owner Password</Form.Label>
                          <Input
                            {...form.register(`crm_entries.${index}.super_admin_password`)}
                            type="password"
                            placeholder="Enter password"
                            error={
                              form.formState.errors.crm_entries?.[index]?.super_admin_password
                                ?.message
                            }
                          />
                          <Form.HelperText>Password for the main admin account</Form.HelperText>
                        </Form.Control>
                      </FormSection>

                      <TwoFaContactFields
                        form={form}
                        fieldPrefix={`crm_entries.${index}.`}
                        user={user}
                        purpose="this CRM"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}

        {/* Add CRM Button - Hidden when a Rollout CRM is selected */}
        {!hasRolloutCrmSelected && (
          <Button
            type="button"
            variant="secondary"
            onClick={handleAddCRM}
            className="inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Another CRM
          </Button>
        )}

        {/* Submit Button - Only show once any selected Rollout CRM is connected */}
        {!pendingRolloutCrm && <NextButton isLoading={isLoading} />}

        {pendingRolloutCrm && (
          <div className="text-center py-4">
            <Typography variant="p" className="text-gray-500">
              Please complete the {ROLLOUT_CRMS[pendingRolloutCrm].label} setup above to continue.
            </Typography>
          </div>
        )}
      </form>

      {/* Rollout Connect popup, filtered to the CRM being connected */}
      <IntegrationsModal
        isOpen={connectingCrm !== null}
        onClose={() => {setConnectingCrm(null); setConnectingCrmConsentAcknowledged(false);}}
        selectedOrg={organization}
        onSuccess={() => {
          if (onFubSuccess) onFubSuccess();
        }}
        crmFilter={connectingCrm ?? undefined}
        hideTwilio={true}
        requireAIAcknowledgement={false}
        onboardingConsentAcknowledged={connectingCrmConsentAcknowledged}
      />

      {/* Speculo Connect form for CRMs the team connects by hand */}
      {manualConnectCrm && organization && (
        <ManualCrmConnectModal
          crm={manualConnectCrm}
          organization={organization}
          source="onboarding"
          onClose={() => setManualConnectCrm(null)}
          onSubmitted={() => onFubSuccess?.()}
        />
      )}
    </StageContainer>
  );
};
